import React, { useState, useEffect, useRef } from "react";

const Timer = ({ appData, updateAppData }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const timerRef = useRef(null);

  // Update current time
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Timer logic
  useEffect(() => {
    if (
      appData.activeSession &&
      !appData.isPaused &&
      appData.timeRemaining > 0
    ) {
      timerRef.current = setInterval(() => {
        updateAppData({
          timeRemaining: Math.max(0, appData.timeRemaining - 1),
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [
    appData.activeSession,
    appData.isPaused,
    appData.timeRemaining,
    updateAppData,
  ]);

  // Complete session when timer reaches 0
  useEffect(() => {
    if (appData.activeSession && appData.timeRemaining === 0) {
      completeSession();
    }
  }, [appData.timeRemaining, appData.activeSession]);

  const startManualSession = () => {
    const session = {
      id: Date.now(),
      title: "Manual Focus Session",
      start: new Date().toTimeString().slice(0, 5),
      end: new Date(Date.now() + 25 * 60000).toTimeString().slice(0, 5),
      days: [],
      manual: true,
    };

    updateAppData({
      activeSession: session,
      timeRemaining: 25 * 60, // 25 minutes
      isPaused: false,
    });
  };

  const togglePause = () => {
    updateAppData({
      isPaused: !appData.isPaused,
    });
  };

  const stopSession = () => {
    const minutesCompleted = Math.floor(
      (calculateSessionDuration(appData.activeSession) -
        appData.timeRemaining) /
        60
    );

    updateAppData({
      activeSession: null,
      timeRemaining: 0,
      isPaused: false,
      stats: {
        ...appData.stats,
        totalMinutes: appData.stats.totalMinutes + minutesCompleted,
        todayMinutes: appData.stats.todayMinutes + minutesCompleted,
        totalSessions: appData.stats.totalSessions + 1,
      },
    });
  };

  const completeSession = () => {
    stopSession();
    // You could add notification here
    window.alert("Session completed! Great work!");
  };

  const calculateSessionDuration = (session) => {
    if (!session) return 0;
    const [startHour, startMinute] = session.start.split(":").map(Number);
    const [endHour, endMinute] = session.end.split(":").map(Number);

    let duration = endHour * 60 + endMinute - (startHour * 60 + startMinute);
    if (duration < 0) duration += 24 * 60;

    return duration * 60; // Convert to seconds
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(secs).padStart(2, "0")}`;
  };

  const getProgress = () => {
    if (!appData.activeSession) return 0;
    const totalSeconds = calculateSessionDuration(appData.activeSession);
    return ((totalSeconds - appData.timeRemaining) / totalSeconds) * 100;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100">
      <div className="text-center p-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl text-white mb-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-center mb-2">
            <i className="ph ph-clock text-2xl mr-2 opacity-80"></i>
            <h2 className="text-xl font-semibold opacity-90">
              {appData.activeSession
                ? appData.activeSession.title
                : "No Active Session"}
            </h2>
          </div>
          <div className="text-6xl font-mono font-bold mb-6 tracking-tight">
            {formatTime(appData.timeRemaining)}
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-slate-700/50 rounded-full h-3 mb-6 backdrop-blur-sm">
            <div
              className="bg-gradient-to-r from-blue-400 to-cyan-400 h-3 rounded-full transition-all duration-1000 shadow-lg"
              style={{ width: `${getProgress()}%` }}
            ></div>
          </div>

          {/* Controls */}
          <div className="flex gap-3 justify-center">
            {!appData.activeSession ? (
              <button
                onClick={startManualSession}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 shadow-lg"
              >
                <i className="ph ph-play text-lg"></i>
                Start Manual Session
              </button>
            ) : (
              <>
                <button
                  onClick={togglePause}
                  className="bg-slate-600 hover:bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 shadow-lg"
                >
                  <i
                    className={`ph ${
                      appData.isPaused ? "ph-play" : "ph-pause"
                    } text-lg`}
                  ></i>
                  {appData.isPaused ? "Resume" : "Pause"}
                </button>
                <button
                  onClick={stopSession}
                  className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 shadow-lg"
                >
                  <i className="ph ph-stop text-lg"></i>
                  Stop
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-4 rounded-xl text-center">
          <div className="flex items-center justify-center mb-2">
            <i className="ph ph-calendar-check text-blue-600 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-blue-700">
            {appData.stats.todayMinutes}
          </div>
          <div className="text-sm text-blue-600 font-medium">Minutes Today</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 p-4 rounded-xl text-center">
          <div className="flex items-center justify-center mb-2">
            <i className="ph ph-calendar text-emerald-600 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-emerald-700">
            {appData.stats.weekMinutes}
          </div>
          <div className="text-sm text-emerald-600 font-medium">This Week</div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 p-4 rounded-xl text-center">
          <div className="flex items-center justify-center mb-2">
            <i className="ph ph-target text-purple-600 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-purple-700">
            {appData.stats.totalSessions}
          </div>
          <div className="text-sm text-purple-600 font-medium">
            Total Sessions
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 p-4 rounded-xl text-center">
          <div className="flex items-center justify-center mb-2">
            <i className="ph ph-fire text-orange-600 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-orange-700">
            {appData.stats.streak}
          </div>
          <div className="text-sm text-orange-600 font-medium">Day Streak</div>
        </div>
      </div>
    </div>
  );
};

export default Timer;
